import { useState } from "react";
import { Link } from "react-router-dom";
import { IconMinus, IconPlus, IconTrash } from "@tabler/icons-react";
import type { ProductVariantDto } from "../../../../api";
import { Image } from "../../../../components/common/image";
import { Button } from "../../../../components/common/button";
import { Text } from "../../../../components/common/typography";
import { CurrencyAmount } from "../../../../components/common/currency_amount";
import { GatedPrice } from "../../../../components/common/gated-price";
import {
  getProductUnitPrice,
  getProductLineTotal,
} from "../../../products/utils/get-pricelist-price";
import { getProductDetailsUrl } from "../../../products/utils/get-product-details-url";
import { WarrantySelectModal } from "./warranty-select-modal";
import {
  useCartStore,
  type CartLineWarranty,
} from "../../../../hooks/stores/useCartStore";
import { useProductWarrantyOptions } from "../../hooks/useProductWarrantyOptions";

interface BasketItemCardProps {
  product: ProductVariantDto;
  quantity: number;
  warranty?: CartLineWarranty | null;
  currency?: string;
  onRemove: () => void;
  onUpdateQuantity: (qty: number) => void;
}

export function BasketItemCard({
  product,
  quantity,
  warranty,
  currency = "QAR",
  onRemove,
  onUpdateQuantity,
}: BasketItemCardProps) {
  const [warrantyOpen, setWarrantyOpen] = useState(false);
  const setLineWarranty = useCartStore((s) => s.setLineWarranty);
  const unitPrice = getProductUnitPrice(product, quantity);
  const lineTotal = getProductLineTotal(product, quantity);
  const title = product.displayName ?? product.name;
  const warrantySubtotal = warranty ? warranty.price * quantity : 0;
  // Stock <=0 / null is treated as "uncapped" — Odoo just hasn't populated
  // the field; BE remains the final gate. atMaxStock is the cap reached state.
  const stockCap =
    typeof product.supplierStock === "number" && product.supplierStock > 0
      ? product.supplierStock
      : null;
  const atMaxStock = stockCap != null && quantity >= stockCap;

  // Hide the warranty button entirely when the seller hasn't attached any
  // extended-warranty SKUs to this product. We pre-fetch here (rather than
  // inside the modal only) so the trigger can disappear without a click.
  const { data: warrantyOptions } = useProductWarrantyOptions(
    product.productTemplateId ?? null,
  );
  const hasWarrantyOptions = (warrantyOptions ?? []).length > 0;

  // Template id is what /products/:id expects; fall back to the variant id
  // for legacy cart payloads that pre-date productTemplateId being on the DTO.
  const detailsUrl = getProductDetailsUrl(
    product.productTemplateId ?? product.id,
    product.id,
  );

  return (
    <div className="rounded-2xl border border-borderDefault bg-bgLight p-5">
      <div className="flex gap-4">
        <Link
          to={detailsUrl}
          aria-label={`View ${title}`}
          className="h-32 w-32 shrink-0 overflow-hidden rounded-xl bg-bgWhite hover:opacity-90 transition-opacity"
        >
          <Image
            image={product.image}
            alt={title}
            size="w-full h-full"
            showPlaceholder
          />
        </Link>

        <div className="flex flex-1 flex-col gap-1.5">
          <Link to={detailsUrl} className="w-fit hover:text-brandPrimary transition-colors">
            <Text variant="bodyBold" tone="dark">{title}</Text>
          </Link>
          <div className="flex flex-col gap-0.5">
            {product.defaultCode && (
              <Text variant="bodySm" tone="dark">SKU: {product.defaultCode}</Text>
            )}
            {product.description && (
              <Text variant="bodySm" tone="dark">{product.description}</Text>
            )}
            {warranty ? (
              <Text variant="bodySm" tone="muted">
                Warranty: {warranty.name} (+{currency} {warranty.price.toLocaleString()} per unit)
              </Text>
            ) : (
              <Text variant="bodySm" tone="muted">
                {hasWarrantyOptions ? "No extended warranty selected" : "Standard warranty included"}
              </Text>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove item"
          className="shrink-0 self-start text-textMuted hover:text-accentError transition-colors"
        >
          <IconTrash size={18} />
        </button>
      </div>

      <div className="mb-4 -mt-2 flex gap-4">
        <div className="w-32 shrink-0" />
        <div className="flex-1 border-t border-borderDefault" />
      </div>

      <div className="flex gap-4">
        <div className="w-32 shrink-0" />
        <div className="flex flex-1 items-center justify-between">
          <div className="flex items-center gap-2">
            <Text variant="bodySmBold" tone="body">Quantity:</Text>
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => onUpdateQuantity(quantity - 1)}
                aria-label="Decrease quantity"
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-borderDefault bg-bgWhite hover:bg-bgDefault transition-colors"
              >
                <IconMinus size={12} />
              </button>
              <Text variant="bodyBold" tone="dark" className="w-6 text-center">
                {quantity}
              </Text>
              <button
                type="button"
                onClick={() => onUpdateQuantity(quantity + 1)}
                aria-label="Increase quantity"
                disabled={atMaxStock}
                title={atMaxStock ? `Only ${stockCap} in stock` : undefined}
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-borderDefault bg-bgWhite hover:bg-bgDefault transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-bgWhite"
              >
                <IconPlus size={12} />
              </button>
            </div>
            {atMaxStock && (
              <Text variant="bodyXs" tone="muted">
                Only {stockCap} in stock
              </Text>
            )}
          </div>

          <div className="flex flex-col items-end gap-0.5">
            <Text variant="bodySm" tone="muted">
              <GatedPrice>{currency} {unitPrice.toLocaleString()}</GatedPrice> × {quantity}
              {warranty ? ` + warranty ${currency} ${warrantySubtotal.toLocaleString()}` : ""}
            </Text>
            <CurrencyAmount
              amount={lineTotal + warrantySubtotal}
              currency={currency}
              variant="bodyBold"
              tone="dark"
            />
          </div>
        </div>
      </div>

      {hasWarrantyOptions && (
        <div className="mt-4">
          <Button
            variant="primary"
            size="sm"
            className="px-5"
            onClick={() => setWarrantyOpen(true)}
          >
            {warranty ? "Change Warranty" : "Select a Warranty"}
          </Button>
        </div>
      )}

      {hasWarrantyOptions && (
        <WarrantySelectModal
          open={warrantyOpen}
          productName={title}
          productTemplateId={product.productTemplateId ?? null}
          selectedWarrantyProductId={warranty?.productId ?? null}
          onClose={() => setWarrantyOpen(false)}
          onConfirm={(selection) => setLineWarranty(product.id, selection)}
        />
      )}
    </div>
  );
}
